import { useField } from 'formik';
import { FormLabel, FormControl } from '@mui/material';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';

import { controlError } from 'utils';

export interface DatePickerFieldProps extends Omit<
  DatePickerProps,
  'label' | 'value'
> {
  name: string;
  label: string;
  format?: string;
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  labelOutside?: boolean;
  hideFormatText?: boolean;
}

export const DatePickerField = ({
  name,
  label,
  required,
  className,
  fullWidth,
  placeholder,
  hideFormatText,
  labelOutside = true,
  format = 'MM/DD/YYYY',
  ...props
}: DatePickerFieldProps) => {
  const [field, meta, helpers] = useField(name);
  type PickerTextFieldSlotProps = Exclude<
    NonNullable<NonNullable<DatePickerProps['slotProps']>['textField']>,
    (ownerState: unknown) => unknown
  >;

  const {
    onClose: onDateClose,
    onChange: onDateChange,
    slotProps: externalSlotProps,
    ...datePickerProps
  } = props;

  const externalTextFieldSlotProps: PickerTextFieldSlotProps =
    typeof externalSlotProps?.textField === 'function'
      ? ({} as PickerTextFieldSlotProps)
      : ((externalSlotProps?.textField ?? {}) as PickerTextFieldSlotProps);

  const externalTextFieldMeta = externalTextFieldSlotProps as unknown as {
    sx?: object;
    onBlur?: () => void;
    placeholder?: string;
  };

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);
  const isEmpty = !field.value;

  return (
    <FormControl error={hasError} className={className} fullWidth={fullWidth}>
      {label && labelOutside && (
        <FormLabel className="text-black-primary mb-1">
          {label}
          {required && <span className="text-red-primary-primary">*</span>}
        </FormLabel>
      )}
      <DatePicker
        value={field.value === '' ? null : field.value}
        onClose={() => {
          helpers.setTouched(true);
          onDateClose?.();
        }}
        onChange={(value, context) => {
          helpers.setTouched(true, false);
          helpers.setValue(value);
          onDateChange?.(value, context);
        }}
        {...datePickerProps}
        slotProps={{
          ...externalSlotProps,
          textField: {
            ...externalTextFieldSlotProps,
            fullWidth,
            error: hasError,
            label: labelOutside ? undefined : label,
            placeholder: placeholder ?? externalTextFieldMeta.placeholder,
            helperText: hasError
              ? errorText
              : !hideFormatText
                ? format
                : undefined,
            onBlur: () => {
              helpers.setTouched(true);
              (
                externalTextFieldSlotProps as { onBlur?: () => void }
              ).onBlur?.();
            },
            sx: {
              '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionContent':
                {
                  color: isEmpty ? 'text.disabled' : 'text.primary',
                },
              ...externalTextFieldMeta.sx,
            },
          } as unknown as PickerTextFieldSlotProps,
        }}
      />
    </FormControl>
  );
};
